import { businessAppRoot } from 'constants.ts';

export const BUSINESS_ROUTES = {
    ROOT: businessAppRoot,
    ONBOARDING: 'onboarding',
    ONBOARDING_VERIFY_EMAIL: 'verify-email',
    ONBOARDING_PHONE: 'phone',
    ONBOARDING_VERIFY_PHONE: 'verify-phone',
    ONBOARDING_SQUARE_STATUS: 'square-status',
    ONBOARDING_PRE_QUAL_STATUS: 'pre-qual-status',
    ONBOARDING_PRE_QUAL_REAPPLY: 'pre-qual-reapply',
    ONBOARDING_REVIEW: 'review',

    PENDING_APPLICATION: 'pending-application',
    PAYMENT_APPROVALS: 'payment-approvals',

    OVERVIEW: 'overview',
    OVERVIEW_ITEM: ':id',
    DEAL_REQUEST: 'request',
    DEAL_REQUEST_ITEM: ':requestId',

    MANAGE: 'manage',
    ACCOUNT: 'account',
    STRUCTURE: 'structure',
    ACCOUNTS: 'accounts',
    CONNECTIONS: 'connections',
    POS: 'pos',
    QUICKBOOKS: 'quickbooks',
    TAXES: 'taxes',
    WORKSPACE: 'workspace',
} as const;
export type BUSINESS_ROUTES = (typeof BUSINESS_ROUTES)[keyof typeof BUSINESS_ROUTES];

export const businessOnboardingPath = [BUSINESS_ROUTES.ROOT, BUSINESS_ROUTES.ONBOARDING].join('/');
export const businessOnboardingVerifyEmailPath = [
    BUSINESS_ROUTES.ROOT,
    BUSINESS_ROUTES.ONBOARDING,
    BUSINESS_ROUTES.ONBOARDING_VERIFY_EMAIL,
].join('/');

export const businessOnboardingPhonePath = [
    BUSINESS_ROUTES.ROOT,
    BUSINESS_ROUTES.ONBOARDING,
    BUSINESS_ROUTES.ONBOARDING_PHONE,
].join('/');

export const businessOnboardingVerifyPhonePath = [
    BUSINESS_ROUTES.ROOT,
    BUSINESS_ROUTES.ONBOARDING,
    BUSINESS_ROUTES.ONBOARDING_VERIFY_PHONE,
].join('/');
export const businessSquareStatusPath = [
    BUSINESS_ROUTES.ROOT,
    BUSINESS_ROUTES.ONBOARDING,
    BUSINESS_ROUTES.ONBOARDING_SQUARE_STATUS,
].join('/');
export const businessPreQualStatusPath = [
    BUSINESS_ROUTES.ROOT,
    BUSINESS_ROUTES.ONBOARDING,
    BUSINESS_ROUTES.ONBOARDING_PRE_QUAL_STATUS,
].join('/');
export const businessPreQualReapplyPath = [
    BUSINESS_ROUTES.ROOT,
    BUSINESS_ROUTES.ONBOARDING,
    BUSINESS_ROUTES.ONBOARDING_PRE_QUAL_REAPPLY,
].join('/');
// `?businessId=<id>` on any business-app URL selects that business before the page opens, so a
// link sent from outside the app lands on the business it is about
export const BUSINESS_ID_PARAM = 'businessId';
export const UNNAMED_BUSINESS = 'New business';

export const PHONE_CODE_SENT_AT_KEY = 'business_owner.codeSentAt';
export const PHONE_CODE_RESEND_SECONDS = 60;

export const onboardingIntroSeenKey = (userId: string) => `business_owner.introSeen:${userId}`;

export const SQUARE_PENDING_PREQUAL_KEY = 'square_pending_prequal';
export const PRE_QUAL_PENDING_KEY = 'pre_qual_pending';
export const PRE_QUAL_DRAFT_KEY = 'pre_qual_draft';

export const SQUARE_PRE_QUAL_EVENT_PREFIX = 'Square Pre-Qual';
export const HG_PRE_QUAL_EVENT_PREFIX = 'HG Pre-Qual';
export const businessOnboardingReviewPath = [
    BUSINESS_ROUTES.ROOT,
    BUSINESS_ROUTES.ONBOARDING,
    BUSINESS_ROUTES.ONBOARDING_REVIEW,
].join('/');
export const getBusinessAppPaths = (root: string) => {
    const overview = [root, BUSINESS_ROUTES.OVERVIEW].join('/');
    const dealRequest = [overview, BUSINESS_ROUTES.DEAL_REQUEST].join('/');

    return {
        overview,
        deal: (id: number | string) => `${overview}/${id}`,
        dealRequest: (id: number | string) => `${dealRequest}/${id}`,
        accounts: [root, BUSINESS_ROUTES.ACCOUNTS].join('/'),
        connections: [root, BUSINESS_ROUTES.MANAGE, BUSINESS_ROUTES.CONNECTIONS].join('/'),
    };
};
export type BusinessAppPaths = ReturnType<typeof getBusinessAppPaths>;
export const businessOwnerPaths = getBusinessAppPaths(BUSINESS_ROUTES.ROOT);

export const businessOverviewPath = businessOwnerPaths.overview;
export const businessDealPath = businessOwnerPaths.deal;
export const HOME_PATHS: string[] = [BUSINESS_ROUTES.ROOT, businessOverviewPath];
export const businessDealRequestItemPath = businessOwnerPaths.dealRequest;
export const businessPendingApplicationPath = [
    BUSINESS_ROUTES.ROOT,
    BUSINESS_ROUTES.PENDING_APPLICATION,
].join('/');
export const businessPaymentApprovalsPath = [
    BUSINESS_ROUTES.ROOT,
    BUSINESS_ROUTES.PAYMENT_APPROVALS,
].join('/');
export const businessManagePath = [BUSINESS_ROUTES.ROOT, BUSINESS_ROUTES.MANAGE].join('/');
export const businessAccountPath = [BUSINESS_ROUTES.ROOT, BUSINESS_ROUTES.ACCOUNT].join('/');
export const businessStructurePath = [
    BUSINESS_ROUTES.ROOT,
    BUSINESS_ROUTES.MANAGE,
    BUSINESS_ROUTES.STRUCTURE,
].join('/');
export const businessAccountsPath = businessOwnerPaths.accounts;
export enum BANK_ACCOUNTS_TAB {
    accounts = 'accounts',
    entities = 'entities',
    connections = 'connections',
    transactions = 'transactions',
}
export const businessTaxesPath = [
    BUSINESS_ROUTES.ROOT,
    BUSINESS_ROUTES.MANAGE,
    BUSINESS_ROUTES.TAXES,
].join('/');
export const businessWorkspacePath = [
    BUSINESS_ROUTES.ROOT,
    BUSINESS_ROUTES.MANAGE,
    BUSINESS_ROUTES.WORKSPACE,
].join('/');
export const businessBreadcrumbNameMap = {
    [businessOverviewPath]: 'Overview',
    [businessAccountPath]: 'Account',
    [businessStructurePath]: 'Structure',
    [businessAccountsPath]: 'Bank Accounts',
    [businessTaxesPath]: 'Taxes',
    [businessWorkspacePath]: 'Workspace',
    [businessPendingApplicationPath]: 'Pending Application',
    // [businessReferralsPath]: 'Referrals',
};

export enum ReviewOnboardingStepKey {
    Details = 'PERSONAL_DETAILS',
    Business = 'BUSINESS_DETAILS',
    Company = 'COMPANY_DETAILS',
    CoOwners = 'CO_OWNERS',
    BankAccounts = 'BANK_ACCOUNTS',
    UploadDocuments = 'UPLOAD_DOCUMENTS',
}

export enum BUSINESS_ONBOARDING_STEPS {
    profile_company = 'profile_company',
    business_details = 'business_details',
    co_owners_details = 'co_owners_details',
    personal_details = 'personal_details',
    invite_accountant = 'invite_accountant',
    bank_accounts = 'bank_accounts',
    document_upload = 'document_upload',
}

export const ONBOARDING_STEP_TITLE: Record<BUSINESS_ONBOARDING_STEPS, string> = {
    [BUSINESS_ONBOARDING_STEPS.profile_company]: 'Profile & Company Details',
    [BUSINESS_ONBOARDING_STEPS.business_details]: 'Business Details',
    [BUSINESS_ONBOARDING_STEPS.co_owners_details]: 'Co-Owners',
    [BUSINESS_ONBOARDING_STEPS.personal_details]: 'Personal Details',
    [BUSINESS_ONBOARDING_STEPS.invite_accountant]: 'Invite Accountant',
    [BUSINESS_ONBOARDING_STEPS.bank_accounts]: 'Connect Your Account(s)',
    [BUSINESS_ONBOARDING_STEPS.document_upload]: 'Document Upload',
};

export const REVIEWED_STAGE = {
    closing: 'closing',
    offer: 'offer',
    reviewed: 'reviewed',
} as const;

export type ReviewedStageKey = (typeof REVIEWED_STAGE)[keyof typeof REVIEWED_STAGE];

export const MIN_OWNERSHIP_PERCENTAGE = 1;

export const REVENUE_MONITORING_HINT = 'Contact support to change revenue monitoring';

export type SMBStepProps<UserData = OnboardingBusinessOwner> = {
    userData: UserData;
    registerReviewValidation: (
        stepKey: ReviewOnboardingStepKey,
        validator: () => Promise<{ isValid: boolean; message?: string }>
    ) => void;
    isReview?: boolean;
};

export type OnboardingStepsProps = SMBStepProps<OnboardingStepsData>;
